import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "./App.css";
import analyticsService from "./services/analyticsService";
import { useGAPageTracking } from "./hooks/useGAPageTracking";
import Login from "./pages/login";
import DealerHome from "./pages/DealerHome";
import CarDetails from "./CarDetail";
import Inspection from "./pages/MyCars/Inspection";
import UploadPictures from "./pages/MyCars/UploadPictures";
import PlaceBids from "./pages/MyCars/PlaceBids";
import TakeDelivery from "./pages/MyCars/TakeDelivery";
import NOC from "./pages/MyCars/NOC";
import Header from "./pages/components/Header";
import MyCarsLayout from "./pages/components/MyCarsLayout";
import Payments from "./pages/MyCars/Payments";
import { CarContextProvider } from "./pages/CarContext";
import { UserProvider } from "./contexts/UserContext";
import Signup from "./pages/Signup";
import DealerLogin from "./pages/DealerLogin";
import PaymentPage from "./pages/PaymentPage";
import UploadDocs from "./pages/UploadDocs";
import DealerContract from "./pages/DealerContract";
import NotFoundErrorPage from "./pages/404";
import UnauthorizedErrorPage from "./pages/401";
import ProtectedRoute from "./pages/components/ProtectedRoute";
import RoleProtectedRoute from "./pages/components/RoleProtectedRoute";
import CarDetailScreen from "./pages/CarDetail";
import EvaLogin from "./pages/Evaluator/evaluator_login";
import 'react-toastify/dist/ReactToastify.css';
import DriverLogin from "./pages/Driver/driver_login";
import AnalyticsTest from "./pages/AnalyticsTest";
import { EvaluatorHome } from "./pages/Evaluator/evaluatorHome";
import { DriverHome } from "./pages/Driver/driverHome";
import OTP from "./pages/components/Otp";

import DealerOnboarding from "./pages/dealer-onboarding/DealerOnboarding";
import DealerDetails from "./pages/dealer-onboarding/DealerDetails";
import Subscription from "./pages/dealer-onboarding/Subscription";
import UploadDocuments from "./pages/dealer-onboarding/UploadDocuments";
import Contract from "./pages/dealer-onboarding/Contract";

// Component that handles GA page tracking
function AppWithTracking() {
  return (
    <UserProvider>
      <CarContextProvider>
        <BrowserRouter>
          <AppWithGATracking />
        </BrowserRouter>
      </CarContextProvider>
    </UserProvider>
  );
}

// Component that uses GA tracking inside Router context
function AppWithGATracking() {
  useGAPageTracking();

  return (
    <div className="App">
      <Routes>
        {/* Public routes - no authentication required */}
        <Route path="/" element={<Login />} />
        <Route path="/dealer/register" element={<Signup />} />
        <Route path="/dealer/login" element={<DealerLogin />} />
        <Route path="/evaluatorLogin" element={<EvaLogin />} />
        <Route path="/driverLogin" element={<DriverLogin />} />
        <Route path="/otp" element={<OTP />} /> {/* Single OTP route */}
        <Route path="/unauthorized" element={<UnauthorizedErrorPage />} />
        <Route path="*" element={<NotFoundErrorPage />} />
        {/* Dealer onboarding routes - require authentication but no specific role */}
        <Route
          element={
            <RoleProtectedRoute requiredRole="orguserdealer">
              <DealerOnboarding />
            </RoleProtectedRoute>
          }
        >
          <Route
            path="/dealer-onboarding/dealer-details"
            element={<DealerDetails />}
          />
          <Route
            path="/dealer-onboarding/subscription"
            element={<Subscription />}
          />
          <Route
            path="/dealer-onboarding/upload-docs"
            element={<UploadDocuments />}
          />
          <Route path="/dealer-onboarding/contract" element={<Contract />} />
        </Route>

        {/* Driver routes - require driver role */}
        <Route
          path="/driverHome"
          element={
            <RoleProtectedRoute requiredRole="delivery_driver">
              <DriverHome />
            </RoleProtectedRoute>
          }
        />
        {/* Evaluator routes - require dealer_evaluator permission */}
        <Route
          path="/evaluatorHome"
          element={
            <RoleProtectedRoute requiredPermission="dealer_evaluator">
              <EvaluatorHome />
            </RoleProtectedRoute>
          }
        />
        {/* Dealer routes - require orguserdealer role */}
        <Route
          element={
            <RoleProtectedRoute requiredRole="orguserdealer" checkOnboardingStatus>
              <Header />
            </RoleProtectedRoute>
          }
        >
          <Route path="/DealerHome" element={<DealerHome />} />
          <Route path="/car-details/:carId" element={<CarDetailScreen />} />
          <Route path="/analytics-test" element={<AnalyticsTest />} />
          {/* Nested MyCars routes */}
          <Route element={<MyCarsLayout carCount={0} />}>
            <Route path="/mycars/inspection" element={<Inspection />} />
            <Route path="/mycars/upload-pictures" element={<UploadPictures />} />
            <Route path="/mycars/place-bids" element={<PlaceBids />} />
            <Route path="/mycars/payments" element={<Payments />} />
            <Route path="/mycars/take-delivery" element={<TakeDelivery />} />
            <Route path="/mycars/noc" element={<NOC />} />
          </Route>
        </Route>
      </Routes>
    </div>
  );
}

function App() {
  useEffect(() => {
    // Initialize analytics service
    analyticsService.init();
  }, []);

  return <AppWithTracking />;
}

export default App;